import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../shared/components/Icons';
import { daysBetween, fmtDay, formatCharge, todayIso } from '../../shared/data/common';
import { useBookings } from '../../shared/booking/BookingsContext';
import { cancelInsurance, getInsuranceBooking, quoteInsuranceCancellation } from '../../api/insurance';
import { useAuth } from '../../auth/AuthContext';

const statusText = {
  Confirmed: ['Active', 'live'],
  PartiallyCancelled: ['Partly cancelled', ''],
  Pending: ['Being issued', ''],
  Cancelled: ['Cancelled', 'cancelled'],
  Failed: ['Failed', 'cancelled'],
};
const name = (t) => `${t.firstName} ${t.lastName}`.trim();

/**
 * Live policies for a booking made through the insurance API: status and policy number per
 * traveller and, when `allowCancel`, cancelling some or all of them.
 */
export default function InsurancePolicies({ booking, allowCancel = false }) {
  const { auth } = useAuth();
  const { pathname } = useLocation();
  const { cancelBooking, updateBooking } = useBookings();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, details: null, error: '' });
  const [selected, setSelected] = useState([]);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [done, setDone] = useState('');
  const supplierBookingId = booking?.supplierBookingId;
  const email = booking?.contact?.email;
  const token = auth?.accessToken;

  useEffect(() => {
    if (!supplierBookingId) return undefined;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: '' }));
    getInsuranceBooking(supplierBookingId, email, token, controller.signal)
      .then((details) => {
        setState({ loading: false, details, error: '' });
        setSelected(details.travellers.filter((t) => t.status === 'Active').map((t) => t.travellerId));
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({
          loading: false,
          details: null,
          error: error.status === 404
            ? 'We could not find this policy with the insurer for these details.'
            : error.message || 'Unable to load the latest policy details.',
        });
      });
    return () => controller.abort();
  }, [supplierBookingId, email, token, attempt]);

  // Keep My Trips in step with the insurer if the policy was cancelled elsewhere (e.g. by support).
  const liveStatus = state.details?.status;
  useEffect(() => {
    if (liveStatus === 'Cancelled' && booking && booking.status !== 'cancelled') {
      cancelBooking(booking.id, booking.refund || { amount: 0, note: 'Cancelled with the insurer.' });
    }
  }, [liveStatus, booking, cancelBooking]);

  if (!supplierBookingId) return null;

  const heading = <h3><Icon name="insurance" size={18} /> Your policies</h3>;
  if (state.loading && !state.details) {
    return <section className="panel ins-policies" role="status">{heading}<p className="muted">Fetching your policies from the insurer…</p></section>;
  }
  if (state.error) {
    return (
      <section className="panel ins-policies">
        {heading}
        <p className="text-coral" role="alert">{state.error}</p>
        <button type="button" className="btn btn-outline" onClick={() => setAttempt(attempt + 1)}>Try again</button>
      </section>
    );
  }

  const { details } = state;
  const [label, pillClass] = statusText[details.status] || [details.status, ''];
  const active = details.travellers.filter((t) => t.status === 'Active');
  const startsIn = details.startDate ? daysBetween(todayIso(), details.startDate) : null;
  const tooLate = startsIn != null && startsIn < 2;
  const canCancel = allowCancel && active.length > 0 && ['Confirmed', 'PartiallyCancelled'].includes(details.status);
  const allSelected = selected.length === active.length;

  const getQuote = async () => {
    setBusy(true);
    setActionError('');
    setDone('');
    try {
      setQuote(await quoteInsuranceCancellation(supplierBookingId, allSelected ? [] : selected, token));
    } catch (error) {
      setActionError(error.message || 'We could not get a cancellation quote.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setActionError('');
    try {
      const result = await cancelInsurance(supplierBookingId, quote.cancellationId, token);
      const refunded = (booking.refund?.amount || 0) + result.customerRefund;
      const note = 'Convenience fee is non-refundable.';
      if (result.bookingStatus === 'Cancelled') cancelBooking(booking.id, { amount: refunded, note });
      else updateBooking(booking.id, { refund: { amount: refunded, note } });
      setDone(`Cancelled. ${formatCharge(result.customerRefund)} will be refunded to your original payment method within 5–7 working days.`);
      setQuote(null);
      setAttempt((n) => n + 1);
    } catch (error) {
      setActionError(error.status === 409
        ? error.message
        : `${error.message || 'The insurer did not confirm the cancellation.'} Your policy is still active — please contact support if you need to cancel.`);
      setQuote(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel ins-policies">
      <div className="fbs-head">
        {heading}
        <span className={`status-pill ${pillClass}`}>{label}</span>
      </div>
      <div className="fbs-ids">
        <div><span className="muted small">{booking.supplierCode || details.supplierCode} booking ID</span><b>{details.supplierBookingId}</b></div>
        {details.startDate && <div><span className="muted small">Cover</span><b>{fmtDay(details.startDate)} – {fmtDay(details.endDate)}</b></div>}
        <div><span className="muted small">Amount charged</span><b>{formatCharge(details.totalAmount)}</b></div>
      </div>

      <div className="table-scroll">
        <table className="data-table">
          <thead><tr>{canCancel && <th aria-label="Select" />}<th>Traveller</th><th>Age</th><th>Policy number</th><th>Status</th></tr></thead>
          <tbody>
            {details.travellers.map((t) => (
              <tr key={t.travellerId}>
                {canCancel && (
                  <td>
                    {t.status === 'Active' && (
                      <input type="checkbox" aria-label={`Cancel policy for ${name(t)}`} checked={selected.includes(t.travellerId)}
                        onChange={(e) => setSelected(e.target.checked ? [...selected, t.travellerId] : selected.filter((id) => id !== t.travellerId))} />
                    )}
                  </td>
                )}
                <td><b>{name(t)}</b></td>
                <td>{t.age ?? '—'}</td>
                <td>{t.policyNumber || <span className="muted">Issuing</span>}</td>
                <td><span className={`status-pill ${t.status === 'Active' ? 'live' : t.status === 'Cancelled' ? 'cancelled' : ''}`}>{t.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {done && <div className="api-message success" role="status">{done}</div>}
      {actionError && <div className="api-message error" role="alert">{actionError}</div>}

      {canCancel && (
        <div className="ins-cancel">
          {tooLate ? (
            <p className="muted small">Cover starts {startsIn <= 0 ? 'today or has started' : 'tomorrow'}, so it can no longer be cancelled online.</p>
          ) : !token ? (
            <p className="muted small">
              <Link className="link" to={`/account/login?returnTo=${encodeURIComponent(pathname)}`}>Sign in</Link> with {email} to cancel policies online.
            </p>
          ) : (
            <>
              <p className="muted small">Cancel up to 24 hours before cover starts. The premium is refunded; the convenience fee is not.</p>
              <button type="button" className="btn btn-danger" disabled={busy || !selected.length} onClick={getQuote}>
                {busy && !quote ? 'Checking refund…' : allSelected ? 'Cancel all policies' : `Cancel ${selected.length} polic${selected.length === 1 ? 'y' : 'ies'}`}
              </button>
            </>
          )}
        </div>
      )}

      {quote && (
        <div className="overlay" onClick={() => !busy && setQuote(null)}>
          <div className="overlay-card modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Cancel policies">
            <h3>Cancel {quote.travellerIds.length === active.length ? 'all policies' : `${quote.travellerIds.length} polic${quote.travellerIds.length === 1 ? 'y' : 'ies'}`}?</h3>
            <p className="muted">
              {details.travellers.filter((t) => quote.travellerIds.includes(t.travellerId)).map(name).join(', ')}
            </p>
            <div className="refund-box">
              <div className="summary-line total"><span>Refund</span><b className="text-primary">{formatCharge(quote.customerRefund)}</b></div>
              <p className="muted small">Credited to your original payment method within 5–7 working days. The convenience fee is not refunded. This cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" disabled={busy} onClick={() => setQuote(null)}>Keep policy</button>
              <button type="button" className="btn btn-danger" disabled={busy} onClick={confirm}>{busy ? 'Cancelling…' : 'Yes, cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
